import { Ringtone as RingtoneType } from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { Ringtone as TenantRingtone } from '@weave/schema-gen-ts/dist/shared/phonedata/v1/tenant.pb';
import { logError } from '../../../../../../utils/loggers';

export type RingtoneOption = RingtoneType;

/** Custom value used as the dropdown value for the synthetic "Default Ringtone" consolidated option. */
export const DEFAULT_RINGTONE_VALUE = '__DEFAULT_RINGTONE__';

/** Internal representation of a (possibly consolidated) dropdown option. */
export type ConsolidatedRingtone = {
  ringtoneType: string;
  /** Display name for the option — may be "/"-joined when consolidated. */
  displayName: string;
  /** Source ringtones backing this option. Length > 1 means consolidated. */
  items: RingtoneOption[];
  /** Stable React key derived from mediaItemId or a fallback string. */
  key: string;
};

/**
 * Converts a device make string into a user-friendly display name for the ringtone tooltip.
 * Recognizes common makes like "Yealink" and "Polycom" (case-insensitive) and returns
 * a properly capitalized name. Unrecognized makes are logged and returned as "Other".
 * @param make The raw device make string.
 * @returns A user-friendly display name for the device make.
 */
export const formatMakeName = (make?: string): string => {
  if (!make) return '';
  if (make.toLowerCase() === 'yealink') return 'Yealink';
  if (make.toLowerCase() === 'polycom') return 'Polycom';

  logError(`Unrecognized device make "${make}" in formatMakeName`, {
    name: 'RingtoneDropdown',
    data: { make },
  });
  return 'Other';
};

/**
 * Returns the CSS variable string for a play button's icon color when the option spans
 * multiple device makes. Uses the new --fabric design tokens directly.
 * Yealink → Seaweed 40 (#07A198), Polycom → Rose 60 (#BB136A).
 * Unrecognized makes return undefined (default button color).
 */
export const getMakeButtonColor = (make?: string): string | undefined => {
  if (!make) return undefined;
  if (make.toLowerCase() === 'yealink') return 'var(--fabric-color-seaweed-40)';
  if (make.toLowerCase() === 'polycom') return 'var(--fabric-color-rose-60)';
  return undefined;
};

/**
 * Builds the consolidated dropdown options from a flat list of ringtones.
 * Groups ringtones by ringtoneType when consolidation is requested (or when the type is RINGTONE_SILENT).
 * When consolidating, a synthetic "Default Ringtone" option is prepended from all items with isDefault=true.
 */
export const buildConsolidatedOptions = (
  ringtones: RingtoneOption[],
  consolidateRingtoneType?: boolean
): ConsolidatedRingtone[] => {
  // Group incoming ringtones by ringtoneType, preserving insertion order.
  const groups = new Map<string, RingtoneOption[]>();

  for (const ringtone of ringtones) {
    const type = ringtone.ringtoneType ?? '';
    const existing = groups.get(type);
    if (existing) {
      existing.push(ringtone);
    } else {
      groups.set(type, [ringtone]);
    }
  }

  const result: ConsolidatedRingtone[] = [];

  // When consolidating, prepend a synthetic "Default Ringtone" option that aggregates all
  // ringtones marked isDefault=true from the filtered list.
  if (consolidateRingtoneType) {
    const defaultItems = ringtones.filter((r) => r.isDefault);
    if (defaultItems.length > 0) {
      result.push({
        ringtoneType: DEFAULT_RINGTONE_VALUE,
        displayName: 'Default Ringtone',
        items: defaultItems,
        key: 'default-ringtone-consolidated',
      });
    }
  }

  for (const [ringtoneType, items] of groups) {
    const isSilent = ringtoneType === TenantRingtone.RINGTONE_SILENT;
    // RINGTONE_SILENT is always consolidated (only one entry regardless of how many
    // makes share the same type). Other types are consolidated only when the prop is set.
    const shouldConsolidate = isSilent || consolidateRingtoneType;

    if (shouldConsolidate && items.length > 1) {
      let displayName: string;
      if (isSilent) {
        // Silent shows only the first name — there is no meaningful per-make distinction.
        displayName = items[0]?.displayName ?? '';
      } else {
        // Join all distinct display names with " / " so users can see which makes are represented.
        const uniqueNames = [...new Set(items.map((i) => i.displayName).filter(Boolean))];
        displayName = uniqueNames.join(' / ');
      }
      // Use the first item's mediaItemId as the stable key for the grouped option.
      const key = items[0]?.mediaItemId || `${ringtoneType}-consolidated`;

      result.push({ ringtoneType, displayName, items, key });
    } else {
      // No consolidation needed — emit one option per ringtone.
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        // When NOT consolidating, annotate the default item with "(Default)" so users can identify it
        // without a separate synthetic option. Skip if the displayName already includes "(Default)".
        const shouldAppendDefault =
          !consolidateRingtoneType && item.isDefault && !(item.displayName ?? '').includes('(Default)');
        const displayName = shouldAppendDefault ? `${item.displayName ?? ''} (Default)` : item.displayName ?? '';
        result.push({
          ringtoneType,
          displayName,
          items: [item],
          key: item.mediaItemId || `${ringtoneType}-${i}`,
        });
      }
    }
  }

  return result;
};

/**
 * Sort comparator for consolidated ringtone options.
 * DEFAULT_RINGTONE_VALUE first, then RINGTONE_SILENT, then alphabetically by displayName.
 */
export const sortConsolidatedOptions = (a: ConsolidatedRingtone, b: ConsolidatedRingtone): number => {
  if (a.ringtoneType === DEFAULT_RINGTONE_VALUE) return -1;
  if (b.ringtoneType === DEFAULT_RINGTONE_VALUE) return 1;
  if (a.ringtoneType === TenantRingtone.RINGTONE_SILENT) return -1;
  if (b.ringtoneType === TenantRingtone.RINGTONE_SILENT) return 1;
  return a.displayName.localeCompare(b.displayName);
};

/**
 * Determines whether make-specific colors should be shown on play buttons.
 * Returns true if at least one consolidated option includes multiple distinct device makes
 * (excluding RINGTONE_SILENT items).
 */
export const hasMultipleMakes = (consolidatedOptions: ConsolidatedRingtone[]): boolean => {
  return consolidatedOptions.some((option) => {
    const makes = option.items
      .map((i) => (i.ringtoneType !== TenantRingtone.RINGTONE_SILENT ? i.make?.toLowerCase() : null))
      .filter(Boolean);
    return new Set(makes).size > 1;
  });
};
